import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationService } from '@ov-suite/authguard-angular/lib/authentication/authentication.service';
import { InventoryLocationModel, ProductSkuModel } from '@ov-suite/models-admin';
import { UserModel } from '@ov-suite/models-idm';
import { JournalEventsService, ManualJournalRequest } from '../journal-events.service';

type JournalOperation = 'create' | 'remove';
type JournalTargetType = 'location' | 'user';
type SelectOption = { id: JournalOperation | JournalTargetType; name: string };

@Component({
  selector: 'ov-suite-manual-journal-modal',
  templateUrl: './manual-journal-modal.component.html',
  styleUrls: ['./manual-journal-modal.component.scss'],
})
export class ManualJournalModalComponent implements OnInit {
  readonly ProductSkuModel = ProductSkuModel;

  readonly InventoryLocationModel = InventoryLocationModel;

  readonly UserModel = UserModel;

  readonly operationOptions: SelectOption[] = [
    { id: 'create', name: 'Create' },
    { id: 'remove', name: 'Remove' },
  ];

  readonly targetOptions: SelectOption[] = [
    { id: 'location', name: 'Location' },
    { id: 'user', name: 'User' },
  ];

  loading = false;

  error = '';

  private autoDescription = '';

  currentUserId?: number;

  form = this.formBuilder.group({
    operation: [this.operationOptions[0], Validators.required],
    targetType: [this.targetOptions[0], Validators.required],
    productSku: [null as ProductSkuModel, Validators.required],
    location: [null as InventoryLocationModel],
    user: [null as UserModel],
    amount: [null as number, [Validators.required, Validators.min(1)]],
    description: ['', Validators.required],
  });

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly journalEventsService: JournalEventsService,
    private readonly authenticationService: AuthenticationService,
  ) {
    this.currentUserId = this.authenticationService.user.value?.id;
  }

  ngOnInit(): void {
    this.configureTargetValidators();

    this.form.get('targetType').valueChanges.subscribe(() => {
      this.configureTargetValidators();
      this.syncDescription();
    });

    this.form.get('operation').valueChanges.subscribe(() => this.syncDescription());
    this.form.get('productSku').valueChanges.subscribe(() => this.syncDescription());
    this.form.get('location').valueChanges.subscribe(() => this.syncDescription());
    this.form.get('user').valueChanges.subscribe(() => this.syncDescription());
    this.form.get('amount').valueChanges.subscribe(() => this.syncDescription());
    this.syncDescription();
  }

  get operation() {
    return (this.form.get('operation').value as SelectOption)?.id as JournalOperation;
  }

  get targetType() {
    return (this.form.get('targetType').value as SelectOption)?.id as JournalTargetType;
  }

  get isCreate() {
    return this.operation === 'create';
  }

  get isLocationTarget() {
    return this.targetType === 'location';
  }

  formatProduct = (product: ProductSkuModel) => {
    if (!product) {
      return '';
    }
    return [product.sku, product.name].filter(Boolean).join(' - ');
  };

  formatLocation = (location: InventoryLocationModel) => location?.name ?? '';

  formatUser = (user: UserModel) => {
    if (!user) {
      return '';
    }
    return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || '';
  };

  onTargetTypeChange(): void {
    this.configureTargetValidators();
    this.syncDescription();
  }

  async submit(): Promise<void> {
    this.error = '';
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    if (!this.currentUserId) {
      this.error = 'Missing responsible user';
      return;
    }

    const productSku = this.form.get('productSku').value as ProductSkuModel;
    const amount = Number(this.form.get('amount').value);
    const description = String(this.form.get('description').value ?? '').trim();
    const baseRequest: ManualJournalRequest = {
      action: 'Manual',
      amount,
      description,
      responsibleUserId: this.currentUserId,
      productSkuId: productSku.id,
    };

    if (this.isCreate) {
      if (this.isLocationTarget) {
        baseRequest.toLocationId = (this.form.get('location').value as InventoryLocationModel)?.id;
      } else {
        baseRequest.toHeldByUserId = (this.form.get('user').value as UserModel)?.id;
      }
    } else if (this.isLocationTarget) {
      baseRequest.fromLocationId = (this.form.get('location').value as InventoryLocationModel)?.id;
    } else {
      baseRequest.fromHeldByUserId = (this.form.get('user').value as UserModel)?.id;
    }

    this.loading = true;
    try {
      if (this.isCreate) {
        await this.journalEventsService.createJournal(baseRequest);
      } else {
        await this.journalEventsService.removeJournal(baseRequest);
      }
      this.modal.close(true);
    } catch (error) {
      this.error = this.formatError(error);
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    this.modal.dismiss('cancel');
  }

  private configureTargetValidators(): void {
    const locationControl = this.form.get('location');
    const userControl = this.form.get('user');
    const locationTarget = this.isLocationTarget;

    if (locationTarget) {
      locationControl.setValidators([Validators.required]);
      userControl.clearValidators();
      userControl.reset(null, { emitEvent: false });
    } else {
      userControl.setValidators([Validators.required]);
      locationControl.clearValidators();
      locationControl.reset(null, { emitEvent: false });
    }

    locationControl.updateValueAndValidity({ emitEvent: false });
    userControl.updateValueAndValidity({ emitEvent: false });
  }

  private syncDescription(): void {
    const descriptionControl = this.form.get('description');
    const nextDescription = this.buildDescription();

    if (!descriptionControl.value || descriptionControl.value === this.autoDescription) {
      descriptionControl.setValue(nextDescription, { emitEvent: false });
    }

    this.autoDescription = nextDescription;
  }

  private buildDescription(): string {
    const product = this.form.get('productSku').value as ProductSkuModel;
    const amount = this.form.get('amount').value;

    if (!product || !amount) {
      return `Manual ${this.operation} journal`;
    }

    const targetLabel = this.isLocationTarget
      ? (this.form.get('location').value as InventoryLocationModel)?.name
      : this.formatUser(this.form.get('user').value as UserModel);

    const targetPrefix = this.isCreate ? 'to' : 'from';
    const targetTypeLabel = this.isLocationTarget ? 'location' : 'user';

    if (!targetLabel) {
      return `Manual ${this.operation} ${amount} x ${product.sku} ${targetPrefix} ${targetTypeLabel}`;
    }

    return `Manual ${this.operation} ${amount} x ${product.sku} ${targetPrefix} ${targetTypeLabel} ${targetLabel}`;
  }

  private formatError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const backendError = error.error?.error ?? error.error?.message;
      if (backendError) {
        return backendError;
      }
      return error.message || 'Unable to save journal';
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'Unable to save journal';
  }
}
